import { Injectable } from '@angular/core';
import { Resource, Service } from 'ngx-jsonapi';
import { Observable } from 'rxjs';
import { HttpService } from 'projects/customer/src/app/shared/services/http-service.service';
import { AxiosAuthenticationService } from 'projects/customer/src/app/shared/services/axios-authentication.service';

// OEM-6688: Fulfillment (WMS) and Online Channels (OMS) reports share the
// same grid/wizard/CRUD components; which backend paths a given screen calls
// is derived purely from its URL (see resolveReportSource, used by
// ReportTabsComponent and AddEditManageReportComponent), not from an @Input
// (routed components aren't input-bound without withComponentInputBinding,
// which isn't enabled in app-routing.module.ts).
export type ReportSource = 'wms' | 'oms';

export function resolveReportSource(urlSegment1: string): ReportSource {
    return urlSegment1 === 'online-channel-report' ? 'oms' : 'wms';
}

// Single consolidated reports API (kepler PR #11281 review feedback: one
// route set differentiated by a report_source dynamic key, not two
// separately-named URL namespaces) — GET requests carry report_source as a
// query param, POST/PUT requests carry it in the body. report_schedules
// already worked this way (see createReportSchedule etc. below); this
// generalizes the same convention to the rest of the reports module.
const REPORTS_BASE = 'reports';

const REPORT_PATHS = {
    listPath: REPORTS_BASE,
    countsPath: `${REPORTS_BASE}/report_counts`,
    typesPath: `${REPORTS_BASE}/report_types`,
    fieldsPath: (id) => `${REPORTS_BASE}/${id}/report_fields`,
    createPath: `${REPORTS_BASE}/create_report`,
    updateStatePath: (id) => `${REPORTS_BASE}/${id}/update_report_state`,
    bulkUpdateStatePath: `${REPORTS_BASE}/reports/bulk_update_state`,
    downloadPath: (id) => `${REPORTS_BASE}/${id}/download_reports`
};

@Injectable({
    providedIn: 'root'
})
export class ManageReportService {
    constructor(public httpService: HttpService) {}

    getReportTypes(wh_id?, source: ReportSource = 'wms') {
        let path = `${REPORT_PATHS.typesPath}?report_source=${source}`;
        if (wh_id) {
            path += `&warehouse_id=${wh_id}`;
        }
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    getReportIds(report_id, warehouse_id?, source: ReportSource = 'wms') {
        let path = `${REPORT_PATHS.fieldsPath(report_id)}?report_source=${source}`;
        if (warehouse_id) {
            path = path + `&warehouse_id=` + warehouse_id;
        }
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    // OMS-only — the create-form's "Select Sales Channel" field, shown per
    // report type's own smp_selection attribute (see getReportTypes' data).
    // No WMS equivalent, so no report_source dispatch needed server-side.
    getSellerMarketplaces() {
        return this.httpService.callAuthGetApi(`${REPORTS_BASE}/seller_marketplaces`, 'jsonApi');
    }

    addDefaults(id: any, data) {
        const path = `report/${id}/set_default`;
        return this.httpService.callPutApi(path, data, 'jsonApi');
    }

    resetDefaults(id: any) {
        const path = `frontend/v1/report/${id}/set_reset`;
        return this.httpService.callPutApi(path);
    }

    createReport(data, source: ReportSource = 'wms') {
        return this.httpService.callAuthPostApi(REPORT_PATHS.createPath, { ...data, report_source: source }, 'jsonApi');
    }

    downloadReport(id, warehouse_id?, source: ReportSource = 'wms') {
        let path = `${REPORT_PATHS.downloadPath(id)}?report_source=${source}`;
        if (warehouse_id) {
            path = path + `&warehouse_id=` + warehouse_id;
        }
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    updateReportState(id: any, data, source: ReportSource = 'wms') {
        return this.httpService.callPutApi(REPORT_PATHS.updateStatePath(id), { ...data, report_source: source }, 'jsonApi');
    }

    // AC-6.1/6.2: pre-flight call omits confirm=true and just returns the
    // affected count; pass confirm=true to actually enqueue the bulk job.
    updateReportStateBulk(
        data: { action: string; report_ids?: Array<string | number>; filter?: Record<string, any>; report_tab?: string },
        confirm: boolean = false,
        source: ReportSource = 'wms'
    ) {
        const path = REPORT_PATHS.bulkUpdateStatePath + (confirm ? '?confirm=true' : '');
        return this.httpService.callPutApi(path, { ...data, report_source: source }, 'jsonApi');
    }

    getBulkActivityStatus(bulkActivityId: any) {
        const path = `bulk_activities/${bulkActivityId}`;
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    // Always returns both sources' counts in one payload (report_counts +
    // online_channel_counts) regardless of caller — there's no single
    // "source" to scope this to, so no report_source param.
    getReportCounts() {
        return this.httpService.callAuthGetApi(REPORT_PATHS.countsPath, 'jsonApi');
    }

    getGridData(path) {
        return this.httpService.callAuthGetApi(path, 'jsonApi');
    }

    // No report_source here — makeGridApiCallPath (report-tabs.component.ts's
    // getReportData) always appends its own `?querystring`, so a second `?`
    // on this bare path would produce a malformed URL. report_source is
    // added to that call's own filters object instead, landing as
    // filter[report_source] — resolveReportSource on the kepler side already
    // checks that location as well as the plain query param.
    getListPath() {
        return REPORT_PATHS.listPath;
    }

    // AC-3.4-3.9/AC-4.1: scheduling is shared across both sources — OXM owns
    // a single report_schedule backend resource and fires it repeatedly via
    // its own scheduler, so it's a full CRUD resource, not a one-off trigger.
    createReportSchedule(data) {
        return this.httpService.callAuthPostApi('report_schedules', data, 'jsonApi');
    }

    getReportScheduleById(reportScheduleId: any) {
        return this.httpService.callAuthGetApi(`report_schedules/${reportScheduleId}`, 'jsonApi');
    }

    updateReportSchedule(reportScheduleId: any, data) {
        return this.httpService.callPutApi(`report_schedules/${reportScheduleId}`, data, 'jsonApi');
    }

    updateReportScheduleState(reportScheduleId: any, state: string) {
        return this.httpService.callPutApi(`report_schedules/${reportScheduleId}/update_state`, { state }, 'jsonApi');
    }

    // AC-7.2/7.3: the owner's failed runs / failed email deliveries not shown yet.
    getReportNotifications() {
        return this.httpService.callAuthGetApi('report_schedules/notifications', 'jsonApi');
    }
}
@Injectable({
    providedIn: 'root'
})
export class ConfigurationService extends AxiosAuthenticationService<Configuration> {
    public resource = Configuration;
    public type = 'reports';
}

export class Configuration extends Resource {
    public attributes = {
        type: ''
    };
}
